// Shared building blocks for the patient detail tabs that show a RECORD
// rather than a table (General Info, Health Profile, Upload of Files).
//
// Same type ramp and colors as before — the only change is structure: fields
// were one tall single-file column with 56px gaps, so reading a phone number
// meant scrolling past half a screen of white space. Grouping them into
// bordered cards on a responsive grid keeps every field in the same visual
// language while making the page scannable.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ClassNames from "Helpers/Common";
import "Colors.css";

// The tab row in Pages/Patients/EditViewPatient renders this slot; tab
// components portal their actions into it so a button like "Edit Profile"
// sits with the tabs without lifting its edit state out of the tab.
export const PATIENT_DETAIL_ACTIONS_SLOT = "patient-detail-actions";

export const HeaderActions = ({ children }) => {
  const [slot, setSlot] = useState(null);

  // Resolved after mount: the slot is rendered by the parent page in the same
  // commit, so it exists by the time effects run. Re-checked on every mount so
  // switching tabs re-attaches instead of rendering into a detached node.
  useEffect(() => {
    setSlot(document.getElementById(PATIENT_DETAIL_ACTIONS_SLOT));
  }, []);

  return slot ? createPortal(children, slot) : null;
};

export const FIELD_LABEL_CSS =
  "text-sm leading-5 font-medium light_grayish_navy";
export const FIELD_VALUE_CSS = "text-sm leading-5 font-normal sapphire";

// Older records legitimately have blanks. One consistent placeholder beats a
// blank line, which reads as a rendering bug rather than "not recorded".
const isEmpty = (v) =>
  v === null ||
  v === undefined ||
  v === "" ||
  v === "-" ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0);

export const EMPTY_VALUE = "None";

/**
 * Marks the row whose action was used last. Several of these actions replace
 * the panel with a sub-view or open a modal, so on return there was nothing
 * left saying which row you had acted on. Light green reads as "this one",
 * not as a status the data itself carries.
 */
export const ACTIVE_ROW_CSS = "active-row";

/**
 * The one way a patient detail page says "nothing here": grey, worded, never
 * a dash. A dash reads as a truncated value or a rendering bug; "None" is an
 * answer. `block` adds the padding an empty table body needs to not collapse
 * against the header.
 */
export const None = ({ block = false, label = EMPTY_VALUE }) => (
  <span className={ClassNames("text-gray-400", block && "block py-4 px-3")}>
    {label}
  </span>
);

// Literal class strings only — Tailwind's purge scans this source, so a
// computed `grid-cols-${n}` would be stripped from the production build.
const COLUMN_CSS = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
};

/** One label + value pair. Pass `children` for multi-line values. */
export const Field = ({ label, value, children, className = "" }) => (
  <div className={ClassNames("flex flex-col gap-1 min-w-0", className)}>
    <span className={FIELD_LABEL_CSS}>{label}</span>
    {children !== undefined && children !== null ? (
      <div className={ClassNames(FIELD_VALUE_CSS, "flex flex-col gap-1 break-words")}>
        {children}
      </div>
    ) : isEmpty(value) ? (
      <None />
    ) : (
      <span className={ClassNames(FIELD_VALUE_CSS, "break-words")}>{value}</span>
    )}
  </div>
);

/** A titled card holding a grid of Fields (or any custom content). */
export const Section = ({ title, action, columns = 2, className = "", children }) => (
  <section
    className={ClassNames(
      // Same surface as the shared Table (and the Orders visit card): shadow,
      // bottom border, rounded — so every panel on these tabs sits at the
      // same elevation.
      "shadow border-b border-gray-200 sm:rounded-lg bg-white overflow-hidden",
      className
    )}
  >
    <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50">
      <h2 className="text-xs font-semibold uppercase tracking-wide light_grayish_navy">
        {title}
      </h2>
      {action}
    </header>
    <div className={ClassNames("grid gap-x-8 gap-y-5 px-5 py-4", COLUMN_CSS[columns])}>
      {children}
    </div>
  </section>
);

/**
 * Renders a list of values as stacked lines, or the empty placeholder when
 * there are none — the old markup mapped over possibly-empty arrays and left
 * the field visually blank.
 */
export const ValueList = ({ items, renderItem }) =>
  items && items.length ? (
    items.map((item, index) => (
      <span key={item?.id ?? index}>{renderItem ? renderItem(item, index) : item}</span>
    ))
  ) : (
    <None />
  );
